import {
    ListingStatus,
    OrderStatus,
    PricingUnit,
    Product,
    OfferListingStatus,
    CustomOfferListingStatus,
    RFQStatus,
} from '@steelbuy/ts-shared';
import { FileUploadStatus } from '@steelbuy/ui-primitive';
import { SortCriteriaOption } from '../lib/search-results/SearchResults.enums';

export default {
    addressForm: {
        primaryTagLabel: 'Principal',
        companyName: 'Nombre de la empresa',
        addressLine1: 'Línea de dirección 1',
        addressLine2: 'Línea de dirección 2 (opcional)',
        town: 'Población / Ciudad',
        postcode: 'Código postal',
        primaryAddressNotDeletable:
            'No es posible eliminar su dirección principal. Primero asigne otra dirección como principal.',
        country: 'País',
        phoneNumber: 'Teléfono (opcional)',
        saveAddressButtonLabel: 'Guardar dirección',
        setAsPrimary: 'Establecer como dirección principal',
        updateAddressButtonLabel: 'Guardar cambios',
        state: 'Estado',
    },
    autoRenew: {
        autoRenewHeader: 'Renovación automática del anuncio',
        autoRenewHelper:
            'El anuncio se renovará automáticamente si no se vende tras la fecha de caducidad, y se enviarán notificaciones previas en caso de cambios. Esto mejora la visibilidad del anuncio y amplía su alcance a posibles compradores.',
        autoRenewLabel: 'Renovación automática del anuncio',
    },
    cardContentCheckoutAddresses: {
        listHeading: 'Dirección de recogida',
        notificationMessage: 'Seleccione la dirección de recogida para que recojamos el material.',
    },
    checkoutDeliveryAddress: {
        listHeading: 'Dirección de entrega',
        notificationMessage: 'Seleccione la dirección de entrega para que enviemos el material.',
    },
    common: {
        edit: 'Editar',
        delete: 'Eliminar',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        back: 'Volver',
        saveChanges: 'Guardar cambios',
        remove: 'Eliminar',
        retry: 'Reintentar',
        save: 'Guardar',
    },
    commonList: {
        loadMore: 'Cargar más',
        noListCaption: 'No se han encontrado resultados',
        buyNow: 'Comprar ahora',
        addToCart: 'Añadir al carrito',
        addedToCart: 'Añadido al carrito',
        new: 'Nuevo',
    },
    confirmSelectionModal: {
        confirmLabel: 'Continuar',
        header: 'Los cambios afectarán sus selecciones en la siguiente pantalla. ¿Desea continuar?',
    },
    createListingTeaser: {
        attributeAge: 'Antigüedad',
        attributeSku: 'ID de material',
        expirationTime: 'Caducidad: ',
    },
    customPrompt: {
        discardButtonLabel: 'Descartar cambios',
    },
    deleteDraftModal: {
        confirmButtonLabel: '¡Sí, eliminar!',
        textContent: '¿Seguro que desea eliminar este borrador?',
    },
    emailAddressForm: {
        emailLabel: 'Correo electrónico',
        submitLabel: 'Actualizar correo electrónico',
        enterValidEmail: 'Introduzca una dirección de correo válida',
        emailNotUpdated: 'El correo electrónico no se ha actualizado',
    },
    fileInput: {
        showMoreLabel: 'Mostrar más',
        showLessLabel: 'Mostrar menos',
        viewButton: 'Ver',
        fileInputLabel: 'Seleccione archivos',
        filesUploaded: '{{uploadedCount}} de {{totalFiles}} subidos',
        filesizeKB: '{{size}} KB',
        filesizeMB: '{{size}} MB',
        sizeAndStatus: '{{size}} – {{status}}',
        fileUploadStatus: {
            [FileUploadStatus.Uploading]: 'Subiendo',
            [FileUploadStatus.Error]: 'Error',
            [FileUploadStatus.Success]: 'Subido',
        },
    },
    login: {
        headerTitle: 'Iniciar sesión',
        errors: {
            usernameRequired: 'Introduzca su correo',
            passwordRequired: 'Se requiere contraseña',
            passwordFormatError:
                'La contraseña solo puede contener letras, números y caracteres especiales, sin espacios',
            passwordIncorrect: 'Correo o contraseña incorrectos',
            serverError: 'Error interno del servidor',
            temporaryPasswordExpired:
                'La contraseña temporal ha caducado. Expira tras 24 h y solo puede usarse una vez.',
            timeoutError: 'Su sesión ha expirado. Vuelva a introducir su usuario y contraseña temporal.',
            unauthorized: 'No está autorizado para acceder a esta página',
            rateLimit:
                'Ha alcanzado el límite de intentos fallidos de acceso. Por seguridad, inténtelo de nuevo en una hora. Si ha olvidado su contraseña, utilice “¿Olvidó su contraseña?”',
            validationError: 'Error: ha ocurrido un problema.',
            invalidEmail: 'Correo inválido',
        },
        forgotPassword: '¿Olvidó su contraseña?',
        usernameLabel: 'Correo electrónico',
        passwordLabel: 'Contraseña',
        usernamePlaceholder: 'Introducir correo',
        passwordPlaceholder: 'Introducir contraseña',
        submitLabel: 'Iniciar sesión',
        challengeRespond: {
            forcePasswordChange: {
                header: 'Crear nueva contraseña',
                subheading: {
                    welcome: 'Bienvenido/a {{username}}, cree una nueva contraseña para su cuenta.',
                    p2: 'Utilice al menos 12 caracteres: cuanto más largos, mejor.',
                    p3: 'Pruebe a usar una frase compuesta por varias palabras fáciles de recordar.',
                },
                successNotification: 'Su contraseña ha sido cambiada. Inicie sesión para continuar.',
            },
            softwareTokenMfa: {
                header: 'Autenticación de dos factores',
                subheading: 'Por seguridad, se ha activado la autenticación en dos pasos (2FA) en su cuenta.',
                info: 'Introduzca el código de autenticación generado por la aplicación.',
                sixDigitCodeLabel: 'Código de 6 dígitos',
                sixDigitCodePlaceholder: 'Código de verificación de 6 dígitos',
                submitLabel: 'Enviar',
                continueLabel: 'Continuar',
            },
            mfaSetup: {
                header: 'Autenticación de dos factores',
                subheading: 'Por seguridad, se ha activado la autenticación en dos pasos (2FA) en su cuenta.',
                instructions: {
                    downloadAndInstall:
                        'Descargue e instale una aplicación (p. ej. Google Authenticator) en su dispositivo móvil.',
                    scanQRCode: 'Escanee el código QR.',
                    verifyAuthCode: 'Introduzca y verifique el código de autenticación generado por la aplicación.',
                },
                validationFailed: 'Validación fallida',
                codeMismatch: 'Código incorrecto',
                genericError: 'Ocurrió un error; por favor, inténtelo de nuevo más tarde.',
            },
        },
        tempPasswordEmailSent: 'El correo de bienvenida se ha reenviado a {{username}}',
        newAccount: {
            label: '¿Necesita una cuenta?',
            link: 'Solicitar cuenta',
        },
        resetPassword: {
            header: 'Restablecer contraseña',
            tokenExpired:
                'El enlace ha caducado. El enlace de verificación expira a las 24 h y solo puede usarse una vez.',
            resendEmail: 'Reenviar correo',
            successNotification: 'Su contraseña ha sido cambiada. Inicie sesión de nuevo para continuar.',
            expiredPassword: {
                expiredTokenError: {
                    header: 'Enlace de restablecimiento caducado',
                    body: 'El enlace de verificación expira a las 24 h y solo puede usarse una vez',
                },
                unknownTokenError: {
                    header: 'Enlace de contraseña inválido',
                    body: 'El enlace de contraseña es inválido; reenvíelo e inténtelo de nuevo',
                },
                submitLabel: 'Reenviar enlace de restablecimiento de contraseña',
            },
        },
        createNewPassword: {
            header: 'Crear nueva contraseña',
            description: 'Bienvenido {{email}}, por favor crea una nueva contraseña para tu cuenta.',
            successNotification: 'La contraseña se ha creado correctamente. Inicie sesión y continúe.',
            expiredPassword: {
                expiredTokenError: {
                    header: 'Enlace de activación expirado',
                    body: 'El enlace de activación caduca después de 48 horas y solo se puede usar una vez.',
                },
                submitLabel: 'Reenviar correo electrónico',
            },
            tokenExpired:
                'El enlace de activación ha expirado. El enlace de activación caduca a las 48 horas y solo se puede usar una vez.',
        },
        backToLogin: 'Volver al inicio de sesión',
        passwordChangedNotification: 'Su contraseña ha sido cambiada.\\nInicie sesión y continúe.',
        passwordAlreadyCreated: 'El usuario ya ha creado una contraseña para esta cuenta',
    },
    listingBuyerDetailsPrice: {
        cardTitle: 'Peso y precio',
        pricePerUnit: 'Precio por {{pricingUnit}} (incl. entrega)',
        totalExclVat: 'Total (sin IVA)',
        totalExclDelivery: 'Total (excl. entrega)',
        totalInclDelivery: 'Total, incl. envío',
        deliveredTotal: 'Precio total entregado',
        pricePerUnitExclDelivery: 'Precio por {{pricingUnit}}',
        total: 'Total',
        loadingPrice: 'Cargando',
    },
    cart: {
        subtotal: 'Valor subtotal',
        deliveredTotalPrice: 'Precio total entregado',
        exclVat: '(sin IVA)',
        item_one: '({{count}} artículo)',
        item_other: '({{count}} artículos)',
        subtotalWeight: 'Peso subtotal',
    },
    checkoutCart: {
        item_one: '{{count}} artículo',
        item_other: '{{count}} artículos',
    },
    cartModal: {
        pricePerUnit: 'por {{pricingUnit}}, incl. entrega',
    },
    listingDraftTeaser: {
        lastEdited: 'Última modificación',
        draft: 'Borrador',
    },
    listingSellerStatus: {
        [ListingStatus.DRAFT]: 'Borrador',
        [ListingStatus.REVIEW_REQUIRED]: 'En revisión',
        [ListingStatus.REVIEW_REJECTED]: 'Rechazado',
        [ListingStatus.PUBLISHED]: 'Activo',
        [ListingStatus.PURCHASED]: 'Vendido',
        [ListingStatus.WITHDRAWN]: 'Retirado',
        [ListingStatus.EXPIRED]: 'Caducado',
    },
    listingTeaser: {
        expiryDate: 'Caduca: {{expiryDate}}',
    },
    listingSellerTeaser: {
        today: 'Hoy',
    },
    listingDetailsStatus: {
        header: {
            [ListingStatus.DRAFT]: 'Último guardado en:',
            [ListingStatus.REVIEW_REQUIRED]: 'Enviado para revisión el:',
            [ListingStatus.REVIEW_REJECTED]: 'Rechazado el:',
            [ListingStatus.PUBLISHED]: 'Publicado el:',
            [ListingStatus.PURCHASED]: 'Solicitud de compra el:',
            [ListingStatus.WITHDRAWN]: 'Retirado el:',
            [ListingStatus.EXPIRED]: 'Caducado el:',
        },
        expiredInfo:
            'Puede renovar este anuncio si establece una nueva fecha de caducidad y ajustar su precio si lo desea. Luego podrá publicar el anuncio renovado directamente en el mercado.',
    },
    listingDetailsExpiration: {
        title: 'Fecha de caducidad',
        text: 'El anuncio caduca el',
        bulkText: 'Los anuncios expiran el',
        autoRenewLabel: 'Renovación automática del anuncio',
        autoRewewBulkLabel: 'Renovación automática de anuncios',
        autoRenewEnabled: 'Activado',
        autoRenewDisabled: 'Desactivado',
        autoRenewToggleHelper: 'Mejora la visibilidad del anuncio y amplía su alcance a posibles compradores.',
        autoRewewExpiryLabel: 'Nueva fecha de caducidad tras renovación automática',
    },
    expiryTimeSelection: {
        formItemHeader: 'Hora de caducidad del anuncio',
        label: 'La caducidad comienza cuando envía el anuncio para revisión. Durante este período, acepta vender su anuncio exclusivamente en SteelBuy.',
        selectDatePlaceholder: 'Seleccionar fecha de caducidad',
        selectTimePlaceholder: 'Hora',
    },
    forgotPassword: {
        title: '¿Olvidó su contraseña?',
        subheading: 'Introduzca su usuario y le enviaremos un correo con instrucciones para restablecer su contraseña',
        submitLabel: 'Enviar correo de restablecimiento',
        enterValidEmail: 'Introduzca una dirección de correo válida',
    },
    passwordLinkSent: {
        title: 'Enlace de restablecimiento enviado',
        subheading: 'Si encuentra algún problema, contacte con soporte.',
        notification:
            'Si existe una cuenta para {{username}}, le enviaremos instrucciones para restablecer la contraseña.',
        backToSignIn: 'Volver a iniciar sesión',
    },
    listingDetailsViews: {
        header: 'Rendimiento',
        numberOfViews: 'Número de vistas',
    },
    listingDetailsMaterial: {
        detailsTitle: 'Detalles',
        skuLabel: 'Su ID de material',
    },
    listingTeaserContainer: {
        buyerPurchaseOrderNumber: 'Su número de orden de compra:',
        steelBuyPurchaseOrderNumber: 'Número de pedido SteelBuy:',
        sellerMaterialId: 'Su ID de material:',
        negotiationReference: 'Referencia de negociación:',
        materialLocation: 'Ubicación del material:',
        weight: 'Peso:',
        origin: 'Origen:',
        age: 'Antigüedad:',
        prime: 'Primera calidad',
        nonPrime: 'Segunda calidad',
        fileName: 'Nombre del archivo:',
    },
    listingTeaserOfferBadge: {
        status: {
            [OfferListingStatus.ACCEPTED]: 'Aceptado',
            [OfferListingStatus.AWAITING_SELLER]: 'Esperando la respuesta del vendedor',
            [OfferListingStatus.AWAITING_BUYER]: 'Esperando la respuesta del comprador',
            [OfferListingStatus.REJECTED]: 'Rechazado',
            [OfferListingStatus.EXPIRED]: 'Caducado',
            [OfferListingStatus.WITHDRAWN]: 'Retirado',
            [OfferListingStatus.CANCELLED]: 'Cancelado',
            [CustomOfferListingStatus.NO_SUCCESSFUL_OFFERS]: 'Sin ofertas exitosas',
            [OfferListingStatus.PURCHASED]: 'Vendido',
        },
        awaitingYourResponse: 'Esperando su respuesta',
        outbid: 'Superado',
        numberOffersHasLabel: 'Este anuncio tiene {{numberOffers}} ofertas',
        numberOffersHadLabel: 'Este anuncio tuvo {{numberOffers}} ofertas',
        numberOffersHasOneLabel: 'Este anuncio tiene {{numberOffers}} oferta',
        offerInProgress: 'Oferta en curso',
    },
    rfqTeaserStatusBadge: {
        status: {
            [RFQStatus.REJECTED]: 'Rechazado',
            [RFQStatus.LIVE]: 'Activo',
            [RFQStatus.WITHDRAWN]: 'Retirado',
            [RFQStatus.EXPIRED]: 'Caducado',
            [RFQStatus.UNDER_REVIEW]: 'En revisión',
        },
    },
    rfqBuyerTeaser: {
        expiryDate: 'Caduca: {{-expiryDate}}',
    },
    rfqForm: {
        definition: {
            description: 'Puede seleccionar una o varias definiciones a la vez.',
        },
        plateType: {
            description: 'Puede seleccionar uno o varios tipos a la vez.',
        },
    },
    rfqDraftTeaser: {
        lastEdited: 'Última modificación',
        infoFieldsMissing_one: 'Información faltante en {{ count }} campo',
        infoFieldsMissing_other: 'Información faltante en {{ count }} campos',
        draft: 'Borrador',
    },
    listingTeaserPrice: {
        detailsButton: 'Detalles',
        offerRange: 'Rango de oferta:',
    },
    marketingBannerSteelbuyPromise: {
        header: 'La promesa de SteelBuy',
        promiseSeller: 'Todos los clientes son verificados por nosotros',
        promiseProcess: 'Proceso de pedido totalmente gestionado',
    },
    inputCharacterCount: {
        inputCharacterRemaining_one: 'Te quedan {{remainingCharactersCount}} carácter',
        inputCharacterRemaining_other: 'Le quedan {{remainingCharactersCount}} caracteres',
    },
    materialAdditionalComment: {
        description:
            'Por favor, proporcione comentarios adicionales sobre la calidad deseada de los materiales para la solicitud inmediata de material.',
        placeholder:
            'Por favor, proporcione comentarios adicionales sobre la calidad deseada de los materiales para la solicitud inmediata de material.',
        label: 'Comentarios (opcional)',
        header: 'Comentarios adicionales',
        maxCharacterError: 'Se ha excedido el límite máximo de caracteres.',
    },
    materialDimensionSelection: {
        toleranceThicknessLabel: 'Tolerancia de espesor',
    },
    materialTestCert: {
        header: 'Certificado/carta de prueba',
        uploadNewFile: 'Subir nuevo archivo',
        testCertToFollow: 'Certificado de prueba/carta de conformidad a seguir',
        requestCertificate: 'Solicitar certificado',
        requestCertificateSuccess: 'Su solicitud de certificado de prueba se envió exitosamente al vendedor.',
        requestCertificateError: 'No se pudo enviar su solicitud de certificado de prueba.',
    },
    materialQualitySelection: {
        descriptionLabel: 'Descripción detallada',
        descriptionPrimeLabel: 'Descripción detallada (opcional)',
        descriptionPlaceholder:
            'Por favor, describa el estado exacto del material para que los compradores puedan evaluar su calidad.',
        descriptionPrimePlaceholder:
            'Por favor, introduzca aquí cualquier información adicional, p. ej., fábrica u origen del material si se sube una carta de conformidad.',
        pictureUploadLabel: 'Subir imagen (opcional)',
        testCertificateLabel: 'Subir certificación de fábrica',
        testCertificateLabelOptional: 'Subir certificación de fábrica (opcional)',
    },
    materialSelection: {
        placeholder: 'Elija una opción',
        otherLabel: 'Otro',
    },
    materialInput: {
        placeholder: 'Seleccionar opción',
    },
    gaugeSelection: {
        label: 'Calibre (Espesor)',
    },
    materialOriginSelection: {
        materialOriginSelection: 'Europa Occidental: UE, Reino Unido, Suiza, Noruega',
    },
    materialWeightInput: {
        formItemHeader: 'Peso',
        formItemHeaderTheoretical: 'Peso teórico',
        weightInputLabel: 'Peso total',
        weightInputLabelTheoretical: 'Tenga en cuenta que venderá este artículo únicamente según el peso teórico.',
        theoreticalWeightNotification:
            'Por favor, introduzca todas las dimensiones en el paso anterior y el número de artículos para calcular el peso teórico total.',
    },
    myAlertsDeletionModal: {
        confirmButtonLabel: '¡Sí, eliminar!',
        textContent: '¿Está seguro de que desea eliminar esta alerta?',
    },
    makeAnOfferModal: {
        makeAnOffer: 'Hacer una oferta',
        reviewOffer: 'Revisar oferta',
        submitOffer: 'Enviar oferta',
        review: 'Revisar',
        pricelabel: 'Precio original por {{pricingUnit}}',
        proposedPrice: 'Su precio propuesto por {{pricingUnit}}',
        creditLimitExceed: 'La cantidad introducida supera su límite de crédito.',
        minimumPriceError: 'Por favor ingrese un monto más alto para ajustarse al valor de mercado.',
        priceExceed: 'El monto ingresado es superior al precio original.',
        infoMsg: 'Enviaremos una actualización del vendedor en un plazo de 24 horas.',
        reviewPricingUnit: {
            [PricingUnit.TON]: {
                label: 'Peso',
            },
            [PricingUnit.KILOGRAM]: {
                label: 'Peso',
            },
            [PricingUnit.SHEET]: {
                label: 'Artículos',
            },
            [PricingUnit.PLATE]: {
                label: 'Artículos',
            },
            [PricingUnit.TREAD]: {
                label: 'Artículos',
            },
            [PricingUnit.CWT]: {
                label: 'Peso',
            },
            [PricingUnit.LB]: {
                label: 'Peso',
            },
        },
        total: 'Total',
        totalExcludingDelivery: 'Total (sin entrega)',
        failedToSubmitOffer: 'Error al enviar la oferta. Por favor, inténtelo de nuevo.',
        existingOffer: 'No se puede enviar la oferta porque ya tiene una oferta activa en este anuncio.',
    },
    myAlertsEmptyState: {
        description:
            'Cree alertas con sus criterios de búsqueda buscando el material que necesita. Le notificaremos regularmente sobre resultados coincidentes.',
        searchButtonLabel: 'Iniciar nueva búsqueda',
        caption: 'No hay alertas activas',
    },
    myAlertsList: {
        alertSettingsButtonLabel: 'Ajustes',
        newResultsSeparatorHeader_zero: 'No hay nuevos resultados (últimas 24 horas)',
        newResultsSeparatorHeader_one: '1 nuevo resultado (últimas 24 horas)',
        newResultsSeparatorHeader_other: '{{count}} nuevos resultados (últimas 24 horas)',
        searchSummaryNewResults: '{{value}} nuevo',
        urgent: 'Urgente',
    },
    myAlertsNeedMore: {
        caption: 'Actualmente no hay resultados que coincidan con sus criterios.',
        description:
            'Se le notificará por correo tan pronto como el producto adecuado esté disponible en la plataforma.',
    },
    myAlertsQna: {
        header: 'Preguntas y respuestas',
        items: {
            howDoesAnAlertWork: {
                label: '¿Cómo funcionan las alertas?',
                content:
                    'Busque material y active alertas para que le notifiquemos por correo tan pronto como aparezca un material coincidente en SteelBuy. En “Mis alertas” puede gestionar sus búsquedas notificadas.',
            },
            howDoUrgentRequestsWork: {
                label: '¿Cómo funcionan las solicitudes de material inmediato?',
                content:
                    'Cree una solicitud inmediata de material para vendedores y le notificaremos por correo sobre nuevos resultados coincidentes. Puede encontrar sus solicitudes en Mis alertas.',
            },
            howDoNotificationsWork: {
                label: '¿Con qué frecuencia seré notificado?',
                content:
                    'Si un material que buscó aparece en SteelBuy, enviaremos una notificación a su dirección de cuenta.',
            },
            howToBestUseAlerts: {
                label: '¿Cómo aprovechar mejor las alertas?',
                content:
                    'Active alertas para materiales que necesite con frecuencia o cuando no haya resultados de búsqueda. Así no tendrá que configurar la búsqueda una y otra vez, y podemos notificarle cuando aparezcan nuevas ofertas.',
            },
            howToMuteOrDeleteAlerts: {
                label: '¿Cómo puedo silenciar o eliminar mis alertas?',
                content:
                    'En “Mis alertas” haga clic en la rueda a la derecha de una alerta guardada para ir a la configuración de alertas. Aquí puede eliminar la alerta o silenciar la notificación por correo. Esto es útil si desea mantener la búsqueda para más adelante, pero no necesita el material ahora. Incluso sin notificaciones, la búsqueda seguirá funcionando al hacer clic en ella. Siempre puede reactivar las notificaciones por correo cuando lo desee.',
            },
        },
    },
    numberOfItemsInput: {
        itemLabel: {
            [Product.SHEET]: 'El número total de láminas.',
            [Product.PLATE]: 'El número total de placas.',
            [Product.TREAD]: 'El número total de placas antideslizantes.',
            [Product.FLOOR]: 'El número total de placas de piso.',
        },
    },
    listingDraft: {
        attributeSku: 'Su ID de material',
        attributeWeight: 'Peso',
        attributePrice: 'Precio',
        attributeExpiry: 'Caducidad',
        attributeCertificate: 'Certificación de fábrica',
    },
    numberOfItemsLabel: {
        label: {
            [Product.SHEET]: 'Número de láminas',
            [Product.PLATE]: 'Número de placas',
            [Product.TREAD]: 'Número de placas antideslizantes',
            [Product.FLOOR]: 'Número de placas de piso',
        },
        valueWithLabel: {
            SHEET_zero: '{{count}} láminas',
            SHEET_one: '1 lámina',
            SHEET_other: '{{count}} láminas',
            PLATE_zero: '{{count}} placas',
            PLATE_one: '1 placa',
            PLATE_other: '{{count}} placas',
            TREAD_zero: '{{count}} placas',
            TREAD_one: '1 placa',
            TREAD_other: '{{count}} placas',
            FLOOR_zero: '{{count}} placas',
            FLOOR_one: '1 placa',
            FLOOR_other: '{{count}} placas',
        },
        labelShort: {
            [Product.SHEET]: 'Nº de láminas:',
            [Product.PLATE]: 'Nº de placas:',
            [Product.TREAD]: 'Nº de placas antideslizantes:',
            [Product.FLOOR]: 'Nº de placas de piso:',
        },
    },
    listingSkuInput: {
        inputHeader: 'Su ID de material',
    },
    orderStatus: {
        [OrderStatus.PROCESSING]: 'Pedido en curso',
        [OrderStatus.FREIGHT_BOOKED]: 'Flete reservado',
        [OrderStatus.SHIPPING]: 'Envío',
        [OrderStatus.DELIVERED]: 'Entregado',
    },
    permissionInformation: {
        basic: {
            searchListing: 'Buscar productos',
            alerts: 'Crear alertas',
            drafts: 'Crear borradores de anuncio',
        },
        seller: {
            submitListing: 'Enviar un anuncio',
            editRejectedListing: 'Editar anuncios rechazados',
            renewListing: 'Publicar un anuncio renovado',
        },
        buyer: {
            purchaseProduct: 'Comprar productos',
        },
        manager: {
            editOrganisation: 'Editar la información de la organización',
            user: 'Añadir usuarios y editar permisos de usuario',
            creditLineInformation: 'Ver la información de la línea de crédito de la organización',
        },
    },
    searchResults: {
        dataHeader: {
            alternativeResultsLabel: 'Resultados alternativos',
            totalResultsLabel_zero: 'Sin resultados',
            totalResultsLabel_one: '1 resultado',
            totalResultsLabel_other: '{{count}} resultados',
        },
    },
    searchResultsSorting: {
        inputTitle: 'Ordenar por',
        options: {
            [SortCriteriaOption.RELEVANCE]: 'Relevancia',
            [SortCriteriaOption.EXPIRES_AT_DESCENDING]: 'Fecha de caducidad',
            [SortCriteriaOption.EXPIRES_AT_ASCENDING]: 'Fecha de caducidad',
            [SortCriteriaOption.WEIGHT_ASCENDING]: 'Peso',
            [SortCriteriaOption.WEIGHT_DESCENDING]: 'Peso',
            [SortCriteriaOption.PRICE_ASCENDING]: 'Precio',
            [SortCriteriaOption.PRICE_DESCENDING]: 'Precio',
            [SortCriteriaOption.WIDTH_DESCENDING]: 'Ancho',
            [SortCriteriaOption.WIDTH_ASCENDING]: 'Ancho',
        },
        placeholderText: 'Elija una opción',
    },
    searchResultsTable: {
        newResultsSeparator_zero: 'Sin nuevos resultados (últimas 24 horas)',
        newResultsSeparator_one: '1 nuevo resultado (últimas 24 horas)',
        newResultsSeparator_other: '{{count}} nuevos resultados (últimas 24 horas)',
        alternativeResultsSeparator: 'Resultados alternativos',
    },
    sellingPriceInput: {
        formItemHeader: 'Su precio de venta por {{pricingUnit}}',
        inputLabel: 'El importe que recibirá cuando su material se venda en SteelBuy (excl. IVA)',
    },
    setPassword: {
        passwordCriteria: {
            minChars: 'Usa al menos 12 caracteres',
            specialChars: 'Al menos un carácter especial',
            number: 'Al menos un número',
            lowerCase: 'Al menos una letra minúscula',
            upperCase: 'Al menos una letra mayúscula',
            matching: 'Las contraseñas deben coincidir',
            fieldRequired: 'Este campo es obligatorio',
        },
        incorrectPassword: 'Contraseña incorrecta',
        passwordsMatch: 'Las contraseñas coinciden',
        password: 'Contraseña',
        newPassword: 'Nueva contraseña',
        currentPassword: 'Contraseña actual',
        retypePassword: 'Reescriba la nueva contraseña',
        submit: 'Restablecer contraseña',
        setPassword: 'Establecer contraseña',
        enterPassword: 'Introducir contraseña',
        enterNewPassword: 'Introduzca la nueva contraseña',
        enterRetypePassword: 'Reescriba la nueva contraseña',
        updatePassword: 'Actualizar contraseña',
        enterCurrentPassword: 'Introduzca la contraseña actual',
    },
    validation: {
        requiredField: 'Este campo es obligatorio.',
        numberValue: 'El valor no es un número.',
        numberMinValue: 'Por favor, introduzca un número mayor.',
        numberMaxValue: 'Por favor, introduzca un número menor.',
        letterValue: 'Solo se permiten letras.',
        fileItemSizeError: 'El archivo debe pesar menos de {{sizeLimitInMegabytes}} MB',
        unsupporttedFileType: 'Tipo de archivo no soportado',
        numberLargerThan: 'Debe ser mayor que {{min}}.',
        mustBeLessThan: 'Debe ser menor que el valor máximo.',
        mustBeGreaterThan: 'Debe ser mayor que el valor mínimo.',
        required: 'Obligatorio',
    },
};
